import "survey-core/defaultV2.min.css";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { useRef, useEffect, useCallback, memo, useMemo } from "react";
import customTheme from "../../Incidents/IncidentForm/survey_theme.json";
import { baseUrl } from "../../../utils/commonMethods";
import { taskTypes } from "../../../enums/taskTypes";
import { useDispatch } from "react-redux";
import { setCredentials, logOut } from "../../../features/auth/authSlice";

function AddTasksAndReviewsForm({ handleSave, isReadOnly, taskType }) {
  const dispatch = useDispatch();
  const handlersRef = useRef({ dispatch });
  const activeRequests = useRef(new Set());
  const sendRequestRef = useRef();

  useEffect(() => {
    handlersRef.current.dispatch = dispatch;
  }, [dispatch]);

  const handleTokenRefresh = useCallback((retryCallback) => {
    const refreshXhr = new XMLHttpRequest();
    refreshXhr.open("POST", `${baseUrl}/auth/tokens:refresh`);
    refreshXhr.withCredentials = true;
    refreshXhr.setRequestHeader("Accept", "application/json");

    refreshXhr.onload = () => {
      if (refreshXhr.status === 200) {
        const result = JSON.parse(refreshXhr.response);
        if (result?.user) {
          handlersRef.current.dispatch(
            setCredentials({
              isAuthenticatedUser: true,
              user: result.user,
            })
          );
          retryCallback();
        } else {
          handlersRef.current.dispatch(logOut());
        }
      } else {
        handlersRef.current.dispatch(logOut());
      }
    };

    refreshXhr.send();
  }, []);

  const sendRequest = useCallback(
    (url, options, callback) => {
      const requestId = Symbol();
      activeRequests.current.add(requestId);

      const xhr = new XMLHttpRequest();
      xhr.open("GET", url);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.withCredentials = true;

      xhr.onload = () => {
        activeRequests.current.delete(requestId);
        if (xhr.status === 200) {
          callback(JSON.parse(xhr.response));
        } else if (xhr.status === 401) {
          handleTokenRefresh(() => {
            const retryXhr = new XMLHttpRequest();
            retryXhr.open("GET", url);
            retryXhr.setRequestHeader("Accept", "application/json");
            retryXhr.withCredentials = true;

            retryXhr.onload = () => {
              if (retryXhr.status === 200) {
                callback(JSON.parse(retryXhr.response));
              } else {
                console.error("Retry failed:", retryXhr.status);
                if (options?.setItems) options.setItems([], 0);
              }
            };

            retryXhr.onerror = () => {
              console.error("Retry network error");
              if (options?.setItems) options.setItems([], 0);
            };

            retryXhr.send();
          });
        } else {
          if (options?.setItems) options.setItems([], 0);
        }
      };

      xhr.onerror = () => {
        activeRequests.current.delete(requestId);
        if (options?.setItems) options.setItems([], 0);
      };

      xhr.send();
    },
    [handleTokenRefresh]
  );

  useEffect(() => {
    sendRequestRef.current = sendRequest;
  }, [sendRequest]);

  const initialFormStructure = useMemo(
    () => ({
      title: "",
      pages: [
        {
          name: "page1",
          title: "",
          elements: [
            {
              type: "dropdown",
              name: "taskId",
              title: "Select the Task",
              isRequired: true,
              choicesLazyLoadEnabled: true,
              choices: [],
            },
            {
              type: "comment",
              name: "taskDescription",
              title: `${
                taskType === taskTypes.NORMAL ? "Task" : "Review"
              } Description`,
              isRequired: true,
              readOnly: isReadOnly,
            },
            {
              type: "tagbox",
              name: "assignedTo",
              title: "Assigned To",
              choicesLazyLoadEnabled: true,
              choicesLazyLoadPageSize: 20,
              isRequired: true,
            },
            {
              type: "text",
              name: "dueInDays",
              title: "How long after creation is it due? (in days)",
              isRequired: true,
              inputType: "number",
              min: 0,
              step: 1,
              validators: [
                {
                  type: "regex",
                  text: "Days cannot be Decimal or Minus ",
                  regex: "^-?[0-9]+$",
                },
              ],
            },
            {
              type: "boolean",
              name: "allowedDocumentsToLink",
              title: "Allow documents to be attached",
              isRequired: true,
            },
          ],
        },
      ],
      widthMode: "responsive",
    }),
    [isReadOnly, taskType]
  );

  const survey = useMemo(() => {
    const model = new Model(initialFormStructure);
    model.applyTheme(customTheme);

    model.onChoicesLazyLoad.add((_, options) => {
      const sendRequest = sendRequestRef.current;
      if (!sendRequest) return;

      if (
        options.question.getType() === "tagbox" &&
        options.question.name === "assignedTo"
      ) {
        const url = `${baseUrl}/users/?page=${
          options.skip / options.take + 1
        }&pageSize=${options?.take}&name=${options.filter}`;
        sendRequest(url, options, (data) => {
          options.setItems(
            data.items.map((x) => ({
              value: x.id,
              text: `${x.firstName} ${x.lastName} (${x?.email})`,
            })),
            data.total
          );
        });
      }
      if (
        options.question.getType() === "dropdown" &&
        options.question.name === "taskId"
      ) {
        const taskIdQuestion = options.question;

        if (!taskIdQuestion.taskList) {
          const url = `${baseUrl}/tasks`;
          sendRequest(url, options, (data) => {
            const allTasks =
              data?.filter((d) => d?.taskType?.id === taskType) || [];
            taskIdQuestion.taskList = allTasks;

            const filter = options.filter?.toLowerCase() || "";
            const filteredTasks = allTasks.filter((t) =>
              t.name.toLowerCase().includes(filter)
            );

            options.setItems(
              filteredTasks.map((x) => ({
                value: x.id,
                text: x.name,
              })),
              filteredTasks.length
            );
          });
        } else {
          const allTasks = taskIdQuestion.taskList;
          const filter = options.filter?.toLowerCase() || "";

          const filteredTasks = allTasks.filter((t) =>
            t.name.toLowerCase().includes(filter)
          );

          options.setItems(
            filteredTasks.map((x) => ({
              value: x.id,
              text: x.name,
            })),
            filteredTasks.length
          );
        }
      }
    });

    model.onValueChanged.add((sender, options) => {
      if (options.name === "taskId") {
        const dropdown = sender.getQuestionByName("taskId");
        const selectedId = options.value;
        const taskList = dropdown.taskList || [];
        if (!selectedId) {
          sender.setValue("taskDescription", "");
          return;
        }
        const selectedTask = taskList.find((t) => t.id === selectedId);
        if (selectedTask) {
          sender.setValue("taskDescription", selectedTask.description || "");
        } else {
          sender.setValue("taskDescription", "");
        }
      }
    });

    return model;
  }, [initialFormStructure, taskType]);

  useEffect(() => {
    if (!survey) return;
    survey.onComplete.add(handleSave);
    return () => {
      survey.onComplete.remove(handleSave);
    };
  }, [handleSave, survey]);

  useEffect(() => {
    const requests = activeRequests.current;
    return () => {
      requests?.clear();
    };
  }, []);

  return <Survey model={survey} />;
}

export default memo(AddTasksAndReviewsForm);
